import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { FIVE_MINUTES } from '@constants';
import { getIntegrations } from 'services';
import {
  FetchIntegrationsListAxiosErrorResponse,
  FetchIntegrationsListAxiosSuccessResponse,
  FetchIntegrationsPayload,
} from 'IntegrationModule/types';
import { integrationQueryKeys } from './integrationQueryKeys';
import groupBy from 'lodash/groupBy';
import {
  findMostRecentlyArchivedIntegrationAndOnlyActiveIntegrationInTargetServiceIdGroup,
  getEffectiveState,
} from 'IntegrationModule/utils/utils';
import { Integration, IntegrationState } from 'types';
import { useMemo } from 'react';
import ApiClientManager from 'core/apiClient';

export const useIntegrationsListQuery = (
  params: FetchIntegrationsPayload,
  { enabled }: { enabled?: boolean } = {},
) => {
  const { data, isLoading, ...query } = useAppQuery<
    FetchIntegrationsListAxiosSuccessResponse,
    FetchIntegrationsListAxiosErrorResponse
  >({
    queryKey: integrationQueryKeys.list({
      ...params,
      identifier: `useIntegrationsListQuery`,
    }),
    queryFn: async ({ token }): Promise<FetchIntegrationsListAxiosSuccessResponse> => {
      // Build custom headers if tenant/realmId are provided to override global cache
      const customHeaders =
        params.tenant && params.realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: params.tenant, realmId: params.realmId })
          : undefined;

      // Strip header-only fields from params (they shouldn't be sent as query params)
      const { tenant: _tenant, realmId: _realmId, ...queryParams } = params;

      const response = await getIntegrations({
        params: queryParams,
        token,
        config: customHeaders ? { headers: customHeaders } : undefined,
      });
      return response as FetchIntegrationsListAxiosSuccessResponse;
    },
    staleTime: FIVE_MINUTES,
    enabled,
  });

  const sortedIntegrationsList = useMemo(() => {
    const integrationsList: Integration[] = [];
    const axiosData = data?.data;

    if (!axiosData) return integrationsList;

    const allIntegrationsByTargetServiceIdOfCurrentTeam = groupBy(
      Object.values(axiosData),
      'targetServiceId',
    ) as Record<string, Integration[]>;

    Object.values(allIntegrationsByTargetServiceIdOfCurrentTeam).forEach(
      (integrationsWithSharedTargetServiceId) => {
        const { activeIntegration, mostRecentlyArchivedIntegration } =
          findMostRecentlyArchivedIntegrationAndOnlyActiveIntegrationInTargetServiceIdGroup(
            integrationsWithSharedTargetServiceId,
          );

        if (activeIntegration) {
          integrationsList.push(activeIntegration);
        } else if (mostRecentlyArchivedIntegration) {
          integrationsList.push(mostRecentlyArchivedIntegration);
        }
      },
    );

    const statePriority: Record<string, number> = {
      [IntegrationState.ACTIVE]: 0,
      [IntegrationState.DRAFT]: 1,
      [IntegrationState.SUSPENDED]: 2,
      [IntegrationState.ARCHIVED]: 3,
    };

    return integrationsList.sort((a, b) => {
      const stateA = getEffectiveState(a) ?? IntegrationState.ACTIVE;
      const stateB = getEffectiveState(b) ?? IntegrationState.ACTIVE;
      return (statePriority[stateA] ?? 99) - (statePriority[stateB] ?? 99);
    });
  }, [data]);

  // TO DO: Add onSuccess
  // useEffect(() => {
  //   if(!isFetching && !isError && data){
  //     onSuccess?.();
  //   }
  // }, [onSuccess])

  return {
    integrationsList: sortedIntegrationsList,
    isLoadingIntegrationsList: isLoading,
    /** False while the list is loading, disabled or failed with nothing cached. */
    isIntegrationsListLoaded: data !== undefined,
  };
};
